import { useLayoutEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { PlayerProvider } from './context/PlayerContext';
import { Home } from './screens/Home';
import { Question } from './screens/Question';
import { Registration } from './screens/Registration';
import { Countdown } from './screens/Countdown';
import { Result } from './screens/Result';
// Disabled: keep the invite page available for potential future reintroduction.
// import { SurveyInvite } from './screens/SurveyInvite';
import { SurveyQuestion } from './screens/SurveyQuestion';
import { IdleCountdown } from './components/IdleCountdown';
import { useDeviceDetector } from './hooks/useDeviceDetector';
import { DEFAULT_CITY, CITY_ROUTE, ROUTES, ROUTE_PATHS } from './routes';
import { colors, ColorsType, fonts, FontsType } from './config/theme';
import { LanguageProvider } from './context/LanguageContext';
import { VerifyPlayer } from './components/VerifyPlayer';
import { CityLayout } from './layouts/CityLayout';

// BG IMAGES
import HomeBg from './assets/backgrounds/home.webp';
import RegistrationBg from './assets/backgrounds/pattern.webp';
import TermsBg from './assets/backgrounds/terms.webp';
import logo from './assets/logo.png';
import { GlobalStyles } from './components/GlobalStyles';
import { getValue } from './utils/css';

type Theme = {
  getValue: typeof getValue;
  getFontSize: typeof getValue;
  colors: ColorsType;
  fonts: FontsType;
  isMobile: boolean;
};

const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);

  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  return isMounted;
};

function App() {
  const { isMobile } = useDeviceDetector();

  const isMounted = useIsMounted();

  const theme = useMemo(
    (): Theme => ({
      getValue,
      colors,
      fonts,
      getFontSize: getValue,
      isMobile,
    }),
    [isMobile],
  );

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      {isMounted && (
        <div style={{ opacity: 0, height: 0 }}>
          <img src={logo} width="1" height="1" />
          <img src={HomeBg} width="1" height="1" />
          <img src={RegistrationBg} width="1" height="1" />
          <img src={TermsBg} width="1" height="1" />
        </div>
      )}
      <Router>
        <LanguageProvider>
          <PlayerProvider>
            <Routes>
              <Route path={CITY_ROUTE} element={<CityLayout />}>
                <Route element={<IdleCountdown route={ROUTES.Home} msTimeout={1000 * 60} />}>
                  <Route element={<VerifyPlayer />}>
                    <Route path={ROUTE_PATHS.Question} element={<Question />} />
                    <Route path={ROUTE_PATHS.Countdown} element={<Countdown />} />
                    {/* Disabled: trivia now navigates directly to SurveyQuestion. */}
                    {/* <Route path={ROUTE_PATHS.SurveyInvite} element={<SurveyInvite />} /> */}
                    <Route path={ROUTE_PATHS.SurveyQuestion} element={<SurveyQuestion />} />
                    <Route path={ROUTE_PATHS.Result} element={<Result />} />
                  </Route>
                  <Route path={ROUTE_PATHS.Registration} element={<Registration />} />
                </Route>
                <Route index element={<Home />} />
              </Route>
              <Route path="*" element={<Navigate to={ROUTES.Home({ city: DEFAULT_CITY })} />} />
            </Routes>
          </PlayerProvider>
        </LanguageProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
